import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';

export interface MiningLandSummary {
  landId: string;
  userId: string;
  landType: string;
  xSize: number;
  ySize: number;
  zSize: number;
  generation: number;
  paidReset: boolean;
  createdAt: string;
  updatedAt: string;
}

interface UseMiningLandsReturn {
  lands: MiningLandSummary[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useMiningLands(): UseMiningLandsReturn {
  const [lands, setLands] = useState<MiningLandSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchIdRef = useRef(0);

  const fetchLands = useCallback(async () => {
    const id = ++fetchIdRef.current;
    try {
      setError(null);
      setLoading(true);
      const { data } = await api.get<{ lands: MiningLandSummary[] }>('/arches/mining/lands');
      if (fetchIdRef.current !== id) return;
      setLands(data.lands);
    } catch (err) {
      if (fetchIdRef.current !== id) return;
      setError(err instanceof Error ? err.message : 'Failed to load mining lands');
    } finally {
      if (fetchIdRef.current === id) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchLands();
  }, [fetchLands]);

  return { lands, loading, error, refresh: fetchLands };
}
